<template>
  <div class="modal-container flexcol">
    <div
      class="modal-header flexrow xbetween ycenter px-3 py-2"
      :class="isEdit ? 'edit-school-query' : ''"
    >
      <div class="flexrow ycenter fs-s color-white">
        <i class="bi bi-building fs-m me-2"></i>
        <span>{{ isEdit ? "Edit" : "Add" }} School Query</span>
      </div>
      <ModalCloseButton :color="isEdit ? 'primary' : 'secondary'" />
    </div>
    <div class="modal-body px-3 py-2">
      <form
        class="user-form"
        @submit.prevent="onFormSubmit"
        v-bind:validation-schema="schoolQueryForm"
      >
        <div class="query-form-input-grid mb-3">
          <CustomDropdown
            v-if="SchoolOptions"
            inputId="SchoolProfileId"
            inputLabel="School"
            inputPlaceholder="Select School Name"
            :dropdownItems="SchoolOptions"
            :initialValue="schoolQueryForm.values.SchoolProfileId"
            :isClearable="true"
            :isFilterable="true"
            :isMandatory="true"
            :isFormField="true"
            :isDisabled="isEdit"
          />
        </div>
        <div class="query-form-input-grid mb-3">
          <CustomRadioButton
            inputId="QueryCategoryId"
            inputLabel="Query Category"
            :radioItems="getIdNamePairsFromEnum(SchoolQueryCategoryEnum)"
            :initialValue="schoolQueryForm.values.QueryCategoryId"
            :isMandatory="true"
            :isFormField="true"
            :isDisabled="isEdit"
            :isSmall="true"
          />
          <div class="flexcol ybetween gap-2">
            <CustomRadioButton
              inputId="QuerySourceId"
              inputLabel="Query Source"
              :radioItems="getIdNamePairsFromEnum(SchoolQuerySourceEnum)"
              :isMandatory="true"
              :isFormField="true"
              :initialValue="schoolQueryForm.values.QuerySourceId"
              :isDisabled="isEdit"
              :isSmall="true"
            />
            <div class="flexrow xstart yend gap-2">
              <CustomDatePicker
                inputId="QueryDate"
                inputLabel="Reported On"
                :initialValue="schoolQueryForm.values.QueryDate"
                :isMandatory="true"
                :isFormField="true"
                :isSmall="true"
                :isDisabled="isEdit"
              />
              <CustomDatePicker
                inputId="QueryDeadline"
                inputLabel="Deadline Date"
                :initialValue="
                  schoolQueryForm.values.QueryDeadline
                    ? schoolQueryForm.values.QueryDeadline
                    : undefined
                "
                :isDisabled="isEdit"
                :isFormField="true"
                :isMandatory="true"
                :isSmall="true"
              />
              <i
                v-if="
                  !isEdit &&
                  schoolQueryForm.values.QueryCategoryId &&
                  schoolQueryForm.values.QueryDate
                "
                class="bi bi-calculator fs-s tighter color-info cursor-pointer"
                @click="computeDefaultDeadlineDate"
              >
              </i>
            </div>
          </div>
        </div>
        <div class="query-form-details-grid mb-3">
          <CustomTextArea
            inputId="QueryDescription"
            inputLabel="Issues Description"
            :numberOfRows="5"
            :isMandatory="true"
            :isFormField="true"
            :initialValue="schoolQueryForm.values.QueryDescription"
          />
          <CustomTextArea
            inputId="NextStepPlanned"
            inputLabel="Next Steps"
            :initialValue="
              schoolQueryForm.values.NextStepPlanned
                ? schoolQueryForm.values.NextStepPlanned
                : undefined
            "
            :numberOfRows="5"
            :isFormField="true"
          />
          <CustomTextArea
            inputId="Remarks"
            inputLabel="Remarks"
            :initialValue="
              schoolQueryForm.values.Remarks ? schoolQueryForm.values.Remarks : undefined
            "
            :numberOfRows="5"
            :isFormField="true"
          />
        </div>
        <el-divider content-position="left">
          <div class="fs-xxs fw-bold color-info">Query Resolution</div>
        </el-divider>
        <div class="flexcol flexrow-lg gap-3 mb-3">
          <CustomDatePicker
            inputId="ClosedOnDate"
            inputLabel="Closed On"
            :isFormField="true"
            :isSmall="true"
          />
          <CustomRadioButton
            inputId="QueryStatusId"
            inputLabel="Status"
            :radioItems="getIdNamePairsFromEnum(SchoolQueryStatusEnum)"
            :initialValue="
              schoolQueryForm.values.QueryStatusId
                ? schoolQueryForm.values.QueryStatusId
                : undefined
            "
            :isMandatory="true"
            :isFormField="true"
            :isSmall="true"
          />
          <CustomRadioButton
            inputId="QueryAlignmentId"
            inputLabel="Alignment"
            :radioItems="getIdNamePairsFromEnum(SchoolQueryAlignmentEnum)"
            :initialValue="
              schoolQueryForm.values.QueryAlignmentId
                ? schoolQueryForm.values.QueryAlignmentId
                : undefined
            "
            :isDisabled="isEdit"
            :isFormField="true"
            :isMandatory="true"
            :isSmall="true"
          />
        </div>
        <div class="flexrow gap-3 my-3">
          <button type="submit" class="button small" :class="isEdit ? `primary` : `secondary`">
            {{ isEdit ? "Update" : "Save" }} Query
          </button>
          <div v-if="isEdit" class="button small danger-reverse" @click="closeModal">Cancel</div>
          <div v-else class="button small danger-reverse" @click="schoolQueryForm.resetForm()">
            Reset
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import CustomDatePicker from "@/components/input/CustomDatePicker.vue";
import CustomDropdown from "@/components/input/CustomDropdown.vue";
import CustomRadioButton from "@/components/input/CustomRadioButton.vue";
import CustomTextArea from "@/components/input/CustomTextArea.vue";
import ModalCloseButton from "@/components/modals/ModalCloseButton.vue";
import ThePageTitleContainer from "@/components/ThePageTitleContainer.vue";
import {
  CommunicationTypeEnum,
  SchoolQueryAlignmentEnum,
  SchoolQueryCategoryEnum,
  SchoolQuerySourceEnum,
  SchoolQueryStatusEnum,
} from "@/enums/aem";
import { getDateString, getIdNamePairsFromEnum, showCustomMessage } from "@/methods/utils";
import { useModalStore } from "@/stores/modalStore";
import { useSchoolStore } from "@/stores/schoolStore";
import { useSessionStore } from "@/stores/sessionStore";
import { SchoolQuery, SchoolQueryDisplay } from "@/types/admin";
import { QueryCategoryDefaultDeadlines } from "@/constants/global";
import { toTypedSchema } from "@vee-validate/yup";
import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";
import { defineComponent, onBeforeMount, PropType } from "vue";
import * as yup from "yup";

export default defineComponent({
  name: "SchoolQueryForm",
  components: {
    CustomDatePicker,
    CustomDropdown,
    CustomRadioButton,
    CustomTextArea,
    ThePageTitleContainer,
    ModalCloseButton,
  },
  props: {
    cardData: { type: Object as PropType<SchoolQueryDisplay>, default: null },
    isEdit: { type: Boolean, default: false },
  },
  emits: ["queryUpdated"],
  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const { allApiVerified, UserInfo } = storeToRefs(sessionStore);
    const schoolStore = useSchoolStore();
    const { SchoolOptions, ActiveSchoolProfileId } = storeToRefs(schoolStore);
    const { getSchoolOptions } = schoolStore;
    const modalStore = useModalStore();
    const { closeModal } = modalStore;

    onBeforeMount(async () => {
      await getSchoolOptions();
      if (props.isEdit && props.cardData) {
        schoolQueryForm.setFieldValue("MongoId", props.cardData.MongoId);
        schoolQueryForm.setFieldValue("SchoolProfileId", props.cardData.School.Id);
        schoolQueryForm.setFieldValue("OwnerId", props.cardData.Owner.Id);
        schoolQueryForm.setFieldValue("QueryDate", props.cardData.QueryDate);
        schoolQueryForm.setFieldValue("QueryCategoryId", props.cardData.QueryCategoryId);
        schoolQueryForm.setFieldValue("QuerySourceId", props.cardData.QuerySourceId);
        schoolQueryForm.setFieldValue("QueryDescription", props.cardData.QueryDescription);
        schoolQueryForm.setFieldValue("NextStepPlanned", props.cardData.NextStepPlanned);
        schoolQueryForm.setFieldValue("Remarks", props.cardData.Remarks);
        schoolQueryForm.setFieldValue("QueryDeadline", props.cardData.QueryDeadline);
        schoolQueryForm.setFieldValue("ClosedOnDate", props.cardData.ClosedOnDate);
        schoolQueryForm.setFieldValue("QueryStatusId", props.cardData.QueryStatusId);
        schoolQueryForm.setFieldValue("QueryAlignmentId", props.cardData.QueryAlignmentId);
      } else if (ActiveSchoolProfileId.value) {
        schoolQueryForm.setFieldValue("SchoolProfileId", ActiveSchoolProfileId.value);
      }
    });
    const schoolQuerySchema = toTypedSchema(
      yup.object().shape({
        SchoolProfileId: yup.number().required("*required"),
        QueryDate: yup.string().required("*required"),
        QueryCategoryId: yup.number().required("*required"),
        QuerySourceId: yup.number().required("*required"),
        QueryDescription: yup.string().required("*required"),
        QueryStatusId: yup.number().required("*required").default(SchoolQueryStatusEnum.PENDING),
        QueryDeadline: yup.string().required("*required"),
        QueryAlignmentId: yup.number().required("*required").default(SchoolQueryAlignmentEnum.NO),
        Remarks: yup.string().notRequired(),
        ClosedOnDate: yup.string().notRequired(),
        NextStepPlanned: yup.string().notRequired(),
        MongoId: yup.string().notRequired(),
        OwnerId: yup.number().required().default(UserInfo.value.UserId),
      }),
    );

    function computeDefaultDeadlineDate() {
      if (!schoolQueryForm.values.QueryDate || !schoolQueryForm.values.QueryCategoryId) {
        return;
      }
      const categoryId = schoolQueryForm.values.QueryCategoryId;
      const queryDate = schoolQueryForm.values.QueryDate;
      const deadlineDays = QueryCategoryDefaultDeadlines[categoryId as SchoolQueryCategoryEnum];
      const startDate = new Date(queryDate);
      startDate.setDate(startDate.getDate() + deadlineDays);
      schoolQueryForm.setFieldValue("QueryDeadline", getDateString(startDate));
    }

    const schoolQueryForm = useForm({ validationSchema: schoolQuerySchema });
    async function onValidSubmit() {
      await saveSchoolQueryForm();
    }

    function onFormSubmit() {
      schoolQueryForm.handleSubmit(onValidSubmit, onInvalidSubmit)();
    }

    async function saveSchoolQueryForm() {
      const formData = schoolQueryForm.values as SchoolQuery;
      const response = props.isEdit
        ? await allApiVerified.value.schoolProfileApi.updateSchoolQuery(formData)
        : await allApiVerified.value.schoolProfileApi.saveSchoolQuery(formData);
      if (response.isSuccess) {
        showCustomMessage({
          messageText: "School query saved successfully.",
          messageType: "success",
        });
        schoolQueryForm.resetForm();
        closeModal();
        emit("queryUpdated");
      } else {
        showCustomMessage({ messageText: response.errorMessage, messageType: "error" });
      }
    }

    function onInvalidSubmit() {
      showCustomMessage({
        messageText: "Oops, there seem to be some errors in the form.",
        messageType: "error",
      });
    }

    return {
      CommunicationTypeEnum,
      SchoolQueryCategoryEnum,
      SchoolQuerySourceEnum,
      SchoolQueryAlignmentEnum,
      SchoolQueryStatusEnum,
      schoolQuerySchema,
      schoolQueryForm,
      SchoolOptions,
      computeDefaultDeadlineDate,
      getIdNamePairsFromEnum,
      onFormSubmit,
      closeModal,
    };
  },
});
</script>
<style lang="scss" scoped>
.modal-container {
  width: 90%;
  height: 95vh;
  overflow: hidden;
  background-color: $color-white;
  border-radius: 10px;
  @include media("<=s") {
    width: 95%;
  }
  & .modal-header {
    background-color: $color-secondary;
    &.edit-school-query {
      background-color: $color-primary-light;
    }
  }
  & .modal-body {
    overflow-y: scroll;
    .query-form-input-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      @include media("<=m") {
        grid-template-columns: 1fr;
      }
    }
    .query-form-details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 10px;
      @include media("<=m") {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
